import React, { useEffect, useState, useCallback } from 'react';

import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { FocusedPageHeader } from 'src/components/accessibility';
import {
  AnalyticsProvider,
  useAnalytics,
  withAnalytics,
} from 'src/components/settings/analytics';
import {
  SettingsPageSection,
  SettingsPageWrapper,
  SettingsPageLoadingWrapper,
} from 'src/components/settings/settings.styled';
import { DEFAULT_ROWS_PER_PAGE } from 'src/constants/settings';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useFlag } from 'src/hooks/flag';
import { useIntl } from 'src/hooks/intl';
import { useObjectState } from 'src/hooks/use-object-state';
import { useWorkspaceAdminStatus } from 'src/hooks/use-workspace-admin-status';
import { useParams } from 'src/router/hooks';
import {
  getProjectRepositories,
  getProjectRepositoriesBuildStatuses,
  RepositoryBuildStatus,
} from 'src/sections/project/utils/utils';
import { getCurrentProjectWorkspaceUuid } from 'src/selectors/project-selectors';
import projectUrls from 'src/urls/projects';
import { httpRequest } from 'src/utils/http-request';
import { captureExceptionWithTags } from 'src/utils/sentry';

import ProjectBreadcrumbs from '../../components/breadcrumbs';
import { PROJECT_EVENT_SOURCES } from '../../types';

import AddRepositoriesModal from './add-repositories-modal';
import { ProjectReposEmptyState } from './project-repositories-empty';
import {
  ProjectRepositoriesTable,
  SortField,
} from './project-repositories-table';

type SortOrder = 'asc' | 'desc';

export const messages = defineMessages({
  repositoriesPageTitle: {
    id: 'frontbucket.projectRepositories.page.title',
    description: 'Title for the project repositories list page',
    defaultMessage: 'Repositories',
  },
  addRepositoriesButtonLabel: {
    id: 'frontbucket.projectRepositories.page.addRepositoriesButtonLabel',
    description: 'Title for the `add repositories` modal trigger',
    defaultMessage: 'Add repositories',
  },
  addReposSuccess: {
    id: 'frontbucket.projectRepositories.page.addReposSuccess',
    description: 'Message when repositories have been added successfully',
    defaultMessage:
      'Added {count, plural, one {{count} repository} other {{count} repositories}} to the current project.',
  },
});

const bulkAddRepos = (
  workspaceSlug: string,
  projectKey: string,
  repoIds: string[]
) =>
  httpRequest({
    successCustomHandler: () => null, // empty response on success
  }).post(
    projectUrls.api.internal.addRepositories(workspaceSlug, projectKey, {}),
    {
      repo_uuids: repoIds,
    }
  );

type SortState = {
  field: SortField;
  order: SortOrder;
};

const ProjectRepositoriesLayout = () => {
  const { formatMessage } = useIntl();
  const { showSuccessFlag, showErrorFlag } = useFlag({ enableForPdvs: true });
  const { workspaceSlug, projectKey } = useParams<{
    workspaceSlug: string;
    projectKey: string;
  }>();
  if (!workspaceSlug || !projectKey) {
    throw new Error(
      'This component must be rendered on a route that includes both :workspaceSlug and :projectKey'
    );
  }
  const workspaceUuid = useSelector(getCurrentProjectWorkspaceUuid);

  const {
    publishScreenEvent,
    publishButtonClickEvent,
    publishTrackEvent,
    getNamespacesWithParent,
  } = useAnalytics();

  useEffect(() => {
    publishScreenEvent();
  }, [publishScreenEvent]);

  const [isRepoAddModalOpen, setIsRepoAddModalOpen] = useState(false);
  const [isSubmittingModal, setIsSubmittingModal] = useState(false);
  const { isWorkspaceAdmin, hasError: hasWorkspaceAdminCheckError } =
    useWorkspaceAdminStatus(workspaceUuid || null);
  const isRepoSizeSortEnabled = useStatsigGate(
    StatsigFeatureKeys.enableRepositorySizeColumnAndSorting
  );

  const DEFAULT_PROJECT_REPOSITORIES_SORTING: SortState = {
    field: 'name',
    order: 'asc',
  };

  // page initilization based on the default sort
  const [{ page, pagelen, sort }, setRequestParams] = useObjectState<{
    pagelen: number;
    page: number;
    sort: SortState;
  }>({
    pagelen: DEFAULT_ROWS_PER_PAGE,
    page: 1,
    sort: DEFAULT_PROJECT_REPOSITORIES_SORTING,
  });

  const [
    {
      isInitialLoad,
      isLoading,
      size,
      repositories,
      repositoryUuidToBuildStatus,
      repositoriesWithBuildStatuses,
    },
    setResponseValues,
  ] = useObjectState<{
    isInitialLoad: boolean;
    isLoading: boolean;
    size: number;
    repositories: BB.Repository[];
    repositoryUuidToBuildStatus: Map<string, RepositoryBuildStatus>;
    repositoriesWithBuildStatuses: BB.Repository[];
  }>({
    isInitialLoad: true,
    isLoading: false,
    repositories: [],
    size: 0,
    repositoryUuidToBuildStatus: new Map<string, RepositoryBuildStatus>(),
    repositoriesWithBuildStatuses: [],
  });

  const getRepos = useCallback(async () => {
    setResponseValues({ isLoading: true });
    try {
      const resp = await getProjectRepositories(workspaceSlug, projectKey, {
        page,
        pagelen,
        sort: `${sort.order === 'desc' ? '-' : ''}${sort.field}`,
      });
      setResponseValues({
        isInitialLoad: false,
        isLoading: false,
        size: resp.size,
        repositories: resp.values,
      });
    } catch (e) {
      showErrorFlag(
        e.message,
        undefined,
        undefined,
        'fetch-project-repos-failed-error-flag'
      );
    } finally {
      setResponseValues({
        isLoading: false,
      });
    }
  }, [
    page,
    pagelen,
    projectKey,
    setResponseValues,
    showErrorFlag,
    sort,
    workspaceSlug,
  ]);

  const getRepositoryBuildStatuses = useCallback(
    async (repositoriesToGet: any) => {
      if (!repositoriesToGet.length) {
        return;
      }
      setResponseValues({ isLoading: true });
      try {
        const resp = await getProjectRepositoriesBuildStatuses(
          repositoriesToGet
        );
        if (resp.length) {
          // Map UUID to build status to save on searching later
          const mappedUuidToBuildStatus = resp.reduce<
            Map<string, RepositoryBuildStatus>
          >((map, status) => {
            map.set(status.repository.uuid, status);
            return map;
          }, new Map<string, RepositoryBuildStatus>());
          setResponseValues({
            repositoryUuidToBuildStatus: mappedUuidToBuildStatus,
          });
        }
      } catch (e) {
        // continue regardless
        captureExceptionWithTags(e);
      } finally {
        setResponseValues({ isLoading: false });
      }
    },
    [setResponseValues]
  );

  const updateRepositoriesWithBuildStatuses = useCallback(
    (repos: any, uuidToStatus: any) => {
      if (repos.length && uuidToStatus.size) {
        const updatedRepositories = repos.map((repository: BB.Repository) => {
          const buildStatus = uuidToStatus.get(repository.uuid);
          return {
            ...repository,
            status_counts: buildStatus && buildStatus.status_counts,
            statusUrl: buildStatus && buildStatus.links.builds.href,
          };
        });
        setResponseValues({
          repositoriesWithBuildStatuses: updatedRepositories,
        });
      } else {
        setResponseValues({
          repositoriesWithBuildStatuses: repos,
        });
      }
    },
    [setResponseValues]
  );

  useEffect(() => {
    getRepos();
  }, [getRepos, sort]);

  useEffect(() => {
    getRepositoryBuildStatuses(repositories);
  }, [getRepositoryBuildStatuses, repositories]);

  // This avoids an infinite loop issue where setting `repositories` within
  // getRepositoryBuildStatuses would retrigger the hook.
  useEffect(() => {
    updateRepositoriesWithBuildStatuses(
      repositories,
      repositoryUuidToBuildStatus
    );
  }, [
    updateRepositoriesWithBuildStatuses,
    repositories,
    repositoryUuidToBuildStatus,
  ]);

  const publishAddReposTrackEvent = useCallback(
    (repoUuids: string[]) => {
      publishTrackEvent({
        action: 'addedRepositories',
        attributes: {
          addedRepositories: repoUuids,
        },
      });
    },
    [publishTrackEvent]
  );

  const onAddRepos = useCallback(
    async (repoUuids: string[]) => {
      if (repoUuids.length) {
        try {
          setIsSubmittingModal(true);
          await bulkAddRepos(workspaceSlug, projectKey, repoUuids);
          publishAddReposTrackEvent(repoUuids);
          getRepos();
          setIsRepoAddModalOpen(false);
          showSuccessFlag(
            formatMessage(messages.addReposSuccess, { count: repoUuids.length })
          );
        } catch (e) {
          showErrorFlag(
            e.message,
            undefined,
            undefined,
            'add-repos-error-flag'
          );
        } finally {
          setIsSubmittingModal(false);
        }
      }
    },
    [
      formatMessage,
      getRepos,
      projectKey,
      showErrorFlag,
      showSuccessFlag,
      workspaceSlug,
      publishAddReposTrackEvent,
    ]
  );

  const onOpenAddReposModal = useCallback(() => {
    publishButtonClickEvent('AddRepositories', {
      isProjectEmpty: !size,
    });
    setIsRepoAddModalOpen(true);
  }, [publishButtonClickEvent, size]);

  const setPage = useCallback(
    (newPage: number) => {
      setRequestParams({ page: newPage });
    },
    [setRequestParams]
  );

  const handleSortChange = useCallback(
    (newField: SortField) => {
      const newSortState = {
        field: newField,
        order:
          sort.field === newField
            ? sort.order === 'asc'
              ? ('desc' as const)
              : ('asc' as const)
            : ('asc' as const),
      };
      if (newField === 'size' && !isRepoSizeSortEnabled) {
        return;
      }
      setRequestParams({ sort: newSortState, page: 1 });
    },
    [sort, setRequestParams, isRepoSizeSortEnabled]
  );

  const currentSortField = sort.field;
  const isAscending = sort.order === 'asc';

  const actions =
    /* In case of workspace admin check error, we would rather show the button than hide it.
    Although this will cause non workspace admins to be able to click the button, they won't be
    able to perform any actions in the add repos modal since they don't have the perms.
    This is a better worst case outcome than if we hide the button in case of error because
    then workspace admins will lose functionality because they won't be able to add repos.
    It's also worth noting that the hook calls a 2.0 API so I'd expect fairly high reliability
    such that this error case should be rare. Same case further below on the ProjectReposEmptyState */
    size && (isWorkspaceAdmin || hasWorkspaceAdminCheckError) ? (
      <Button onClick={onOpenAddReposModal}>
        {formatMessage(messages.addRepositoriesButtonLabel)}
      </Button>
    ) : undefined;

  return (
    <SettingsPageSection>
      <SettingsPageWrapper>
        <FocusedPageHeader
          breadcrumbs={<ProjectBreadcrumbs />}
          actions={actions}
        >
          {formatMessage(messages.repositoriesPageTitle)}
        </FocusedPageHeader>
        {!isInitialLoad && !size && (
          <ProjectReposEmptyState
            onAddReposClick={onOpenAddReposModal}
            isWorkspaceAdmin={isWorkspaceAdmin || hasWorkspaceAdminCheckError}
          />
        )}
        {(isInitialLoad || (isLoading && !size)) && (
          // show the spinner if repo data is being refreshed after adding the first repo(s) to the project
          <SettingsPageLoadingWrapper>
            <Spinner size="large" testId="project-repos-spinner" />
          </SettingsPageLoadingWrapper>
        )}
        {!isInitialLoad && !!size && (
          <ProjectRepositoriesTable
            isLoading={isLoading}
            size={size}
            repositories={repositoriesWithBuildStatuses}
            page={page}
            pagelen={pagelen}
            setPage={setPage}
            handleSortChange={handleSortChange}
            sortField={currentSortField}
            isAscending={isAscending}
            isSizeSortEnabled={isRepoSizeSortEnabled}
          />
        )}
        <ModalTransition>
          {isRepoAddModalOpen && (
            <AnalyticsProvider
              data={{
                source: PROJECT_EVENT_SOURCES.ADD_REPOSITORIES_MODAL,
                namespaces: getNamespacesWithParent(
                  PROJECT_EVENT_SOURCES.ADD_REPOSITORIES_MODAL
                ),
              }}
            >
              <AddRepositoriesModal
                isSubmitting={isSubmittingModal}
                onAddRepos={onAddRepos}
                onClose={() => setIsRepoAddModalOpen(false)}
              />
            </AnalyticsProvider>
          )}
        </ModalTransition>
      </SettingsPageWrapper>
    </SettingsPageSection>
  );
};

const AnalyzedProjectRepositoriesLayout = withAnalytics(
  ProjectRepositoriesLayout,
  {
    source: PROJECT_EVENT_SOURCES.PROJECT_REPOSITORIES_SCREEN,
    actionSubject: 'project',
  }
);

export default AnalyzedProjectRepositoriesLayout;
